(ns manage.product-store-form.hooks
  (:require [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.form :as form]
            [common.queries :refer [use-mutation use-suspense-query]]
            [common.resource.query.product-store :as product-store.q]
            [common.util :refer [spread-obj]]
            [malli.core :as m]
            [uix.core :refer [$ defhook defui] :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(defn allowed-chars? [v]
  (re-matches #"[a-zA-Z0-9\-\_]+" v))

(defn field-schema [field-names-set]
  (m/schema
   [:map
    [:name
     [:and
      [:string {:min 1 :max 70
                :error/fn (fn [{:keys [value]} _]
                            (cond
                              (str/blank? value) "The field should not be empty"
                              (> (count value) 70) "Name can not be longer than 70 characters"
                              :else nil))}]
      [:fn {:error/message "Field name should be unique"}
       (fn [s]
         (not (contains? field-names-set s)))]
      [:fn {:error/message "Allowed characters are a-z, 0-9, -, _"}
       (fn [s]
         (and (string? s)
              (allowed-chars? s)))]]]
    [:type
     [:string {:min 1
               :error/message "This field type should not be empty"}]]]))

(defn transform-available-slots
  "Transform available-slots from API format to dropdown options"
  [available-slots]
  (->> available-slots
       (keep (fn [[key value]]
               (when (seq value)
                 {:label (name key)
                  :id (name key)})))
       (into [])))

;; Context for edit state
(def product-store-edit-context (uix/create-context nil))

(defhook use-product-store-edit-state []
  (let [[edit-field-id set-edit-field-id] (uix/use-state nil)
        set-edit-field (uix/use-callback
                        (fn [idx]
                          (set-edit-field-id idx))
                        [])
        clear-edit-field (uix/use-callback
                          (fn []
                            (set-edit-field-id nil))
                          [])]
    {:edit-field-id edit-field-id
     :set-edit-field set-edit-field
     :clear-edit-field clear-edit-field}))

(defui product-store-edit-provider [{:keys [children]}]
  (let [edit-state (use-product-store-edit-state)]
    ($ product-store-edit-context {:value edit-state}
       children)))

(defhook use-product-store-edit-context []
  (let [context (uix/use-context product-store-edit-context)]
    (when (nil? context)
      (throw (js/Error. "use-product-store-edit-context must be used within ProductStoreEditProvider")))
    context))

(defhook use-in-edit? [field-id]
  (let [{:keys [edit-field-id]} (use-product-store-edit-context)]
    (= field-id edit-field-id)))

(defhook use-field [field-id]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        get-field (uix/use-callback (fn [^js data]
                                      (->> (->clj data)
                                           :fields
                                           (some (fn [{:keys [id] :as field}]
                                                   (when (= id field-id)
                                                     field)))))
                                    [field-id])
        {field :data} (use-suspense-query (spread-obj (product-store.q/product-store-query {:advertiser-id advertiser-id})
                                                      #js {:select get-field}))]
    field))

(defhook use-product-store-field-form [field-id]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {add-field :mutate} (use-mutation (product-store.q/add-field-mutation {:advertiser-id advertiser-id}))
        {edit-field :mutate} (use-mutation (product-store.q/edit-field-mutation {:advertiser-id advertiser-id}))
        {:keys [data]} (use-suspense-query (product-store.q/product-store-query {:advertiser-id advertiser-id}))
        field (uix/use-memo (fn []
                              (some (fn [{:keys [id] :as field}]
                                      (when (= id field-id)
                                        field))
                                    (:fields data)))
                            [data field-id])
        schema (uix/use-memo (fn []
                               (let [field-names-set (into #{}
                                                           (comp
                                                            (remove (fn [{:keys [id]}]
                                                                      (= id field-id)))
                                                            (map :name))
                                                           (:fields data))]
                                 (field-schema field-names-set)))
                             [data field-id])
        {:keys [clear-edit-field]} (use-product-store-edit-context)
        new-field? (nil? field)
        available-slots (when data
                          (transform-available-slots (:available-slots data)))
        form-resolver (form/use-malli-resolver schema)
        {:keys [handleSubmit] :as form-control}
        (form/use-form #js {:mode "all"
                            :values (->js (or field
                                              {:name ""
                                               :type ""
                                               :empty-string-is-undefined false}))
                            :resolver form-resolver})
        on-submit (uix/use-callback
                   (fn [values]
                     (if new-field?
                       (add-field
                        (->clj values)
                        #js {:onSuccess (fn [_resp]
                                          (clear-edit-field))})
                       (edit-field
                        (->clj values)
                        #js {:onSuccess (fn [_resp]
                                          (clear-edit-field))})))
                   [field-id new-field? add-field edit-field clear-edit-field])]
    {:form-control form-control
     :on-submit (handleSubmit on-submit)
     :field field
     :available-slots available-slots}))

(defhook use-product-store-move-field []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {move-field :mutate} (use-mutation (product-store.q/move-field-mutation {:advertiser-id advertiser-id}))
        {:keys [data]} (use-suspense-query (product-store.q/product-store-query {:advertiser-id advertiser-id}))
        move-field-fn (uix/use-callback
                       (fn [{:keys [oldIndex newIndex]}]
                         (when (and data (not= oldIndex newIndex))
                           (let [fields (:fields data)
                                 field-to-move (nth fields oldIndex nil)]
                             (when field-to-move
                               (move-field {:field-id (:id field-to-move)
                                            :old-index oldIndex
                                            :new-index newIndex})))))

                       [data move-field])]
    {:move-field move-field-fn}))

(defn- fields-count-select [^js data]
  (->> (->clj data)
       :fields
       count))

(defhook use-product-fields-count []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {:keys [data]} (use-suspense-query (spread-obj (product-store.q/product-store-query {:advertiser-id advertiser-id})
                                                       #js {:select fields-count-select}))]
    data))

(defn- field-ids-select [^js data]
  (->> (->clj data)
       :fields
       (mapv :id)))

(defhook use-product-field-ids []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {:keys [data isError]} (use-suspense-query (spread-obj (product-store.q/product-store-query {:advertiser-id advertiser-id})
                                                               #js {:select field-ids-select}))]
    {:field-ids data
     :is-error isError}))

